import React, { useCallback, useEffect, useRef } from "react"

import Lottie from "react-lottie"

import { Wrapper } from "./Loader.styles"

import * as animationDataModule from "./loader.json"

/**
 * Full-screen intro overlay.
 *
 * Previously it dismissed itself only from the Lottie "complete" event, driven
 * through a gsap timeline. Neither is reliable here: `react-lottie` is
 * unmaintained and does not always emit "complete" under React 18, and an ESM
 * JSON import arrives as a module namespace object rather than the raw
 * animation, which stops it initialising at all. The overlay then sat at
 * opacity 1 forever, covering the entire site with a spinner — in production as
 * well as in development.
 *
 * The dismissal is now plain DOM + CSS, on a timeout, with the Lottie event as
 * an early-exit rather than the only path. A safety net must not depend on the
 * machinery it is protecting against.
 */
const FALLBACK_MS = 1800
const FADE_MS = 450

// ESM interop: `import * as x from "./x.json"` yields a module namespace object.
const animationData = animationDataModule?.default || animationDataModule

const Loader = () => {
  const dismissed = useRef(false)

  const dismiss = useCallback(() => {
    if (dismissed.current || typeof document === "undefined") return
    dismissed.current = true

    const wrapper = document.getElementById("loader-wrapper")
    if (!wrapper) return

    const reduced = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches
    const fade = reduced ? 0 : FADE_MS

    wrapper.style.transition = `opacity ${fade}ms ease`
    wrapper.style.opacity = "0"
    wrapper.style.pointerEvents = "none"

    window.setTimeout(() => {
      wrapper.style.display = "none"
      document.body.style.overflowY = "unset"
    }, fade)
  }, [])

  useEffect(() => {
    const timer = window.setTimeout(dismiss, FALLBACK_MS)
    return () => window.clearTimeout(timer)
  }, [dismiss])

  const defaultOptions = {
    loop: false,
    autoplay: true,
    animationData,
    rendererSettings: { preserveAspectRatio: "xMidYMid slice" },
  }

  return (
    <Wrapper id="loader-wrapper" aria-hidden="true">
      <div id="loader">
        <Lottie
          options={defaultOptions}
          eventListeners={[{ eventName: "complete", callback: dismiss }]}
        />
      </div>
    </Wrapper>
  )
}

export default Loader
